import * as React from 'react';
import cx from 'classnames';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { Field, FieldError } from '../field';
import { IInputComponent } from '@modules/fhq-design-tool/types/input-component';
import { FieldSelector } from '@common/components/FieldSelector';
import { Ellipsis } from '@common/components/Ellipsis';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { QuestionnaireFolderField } from '@modules/fhq-design-tool/types/field';
import isNil from 'lodash/isNil';
import { I18n } from 'react-redux-i18n';
import { FieldSelectorGetFieldParams, FieldSelectorOnChangeSingle } from '@common/components/FieldSelector/field-selector.types';
import { FolderContext } from '@common/dto/folder/context';
import { FieldType } from '@common/dto/field/type';

import './field-picker.styles.scss';

export type FieldPickerOnChangeValue = {
  field: { 
    id: string | number; 
    system: boolean;
    name: string;
    context: FolderContext;
    type: FieldType;
  } 
};

export type FieldPickerProps = IInputComponent<string | undefined, FieldPickerOnChangeValue | undefined> & {
  clearable?: boolean;
  errors?: Array<FieldError>;
  withSearch?: boolean;
  labelWidth?: number | string;
  label: string;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getFields: (params: { folderId?: FolderPrimaryListDto['id']; searchQuery?: string; context: number }) => Promise<Array<QuestionnaireFolderField>>;
  title?: string;
  placeholder?: string | number;
};

export const FieldPicker: React.FC<FieldPickerProps> = (props) => {
  const { placeholder, errors, withSearch, className, disabled, label, labelWidth, onChange, value, metadata, description, required, margin, getFields, getFolders, clearable, title } = props;

  const [selectorOpened, setSelectorOpenedState] = React.useState(false);

  const closeSelector = React.useCallback(() => {
    setSelectorOpenedState(false);
  }, []);

  const openSelector = React.useCallback(() => {
    setSelectorOpenedState(true);
  }, []);

  const handleChange = React.useCallback((
    { field }: FieldSelectorOnChangeSingle
  ) => {
    onChange({
      field: {
        id: field.fieldId,
        name: field.fieldName,
        system: field.systemField,
        context: field.fieldContext,
        type: field.fieldTypeId as FieldType
      },
    });
  }, [onChange]);

  const handleClear = React.useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  return (
    <Field 
      errors={errors}
      labelWidth={labelWidth} 
      label={label} 
      disabled={disabled}
      className={cx('FhqDesignToolFieldPicker', className)}
      description={description}
      required={required}
      margin={margin}
      classes={{
        input: 'FhqDesignToolFieldPicker__input'
      }}
    >
      <button {...getTestAttributes(MODULE_NAME, metadata.description, 'trigger-btn')} className="FhqDesignToolFieldPicker__trigger" onClick={openSelector}>
        <Ellipsis 
          maxWidth="calc(100% - 24px)" 
          text={value ?? placeholder ?? I18n.t('Select Field')} 
          className={isNil(value) ? 'FhqDesignToolFieldPicker__trigger__placeholder' : undefined} 
        />
        <img src="gif/df_warehouse.gif" />
      </button>
      {clearable && !isNil(value) && (
        <button 
          {...getTestAttributes(MODULE_NAME, metadata.description, 'clear-btn')} 
          className="FhqDesignToolFieldPicker__clear-btn"
          onClick={handleClear}
        >
          ✖
        </button>
      )}
      {selectorOpened && (
        <FieldSelector
          withSearch={withSearch}
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => getFields({ searchQuery: searchKeyword, ...restParams })}
          onChange={handleChange}
          close={closeSelector}
          title={title}
        />
      )}
    </Field>
  );
};